import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatColumnDef, MatTable } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FilterComponent } from './filter.component';
import { IFilterEvent } from './models/abstractions/filter.interface';
import { FilterColumn, FilterColumnType, FilterModel, StateModel } from './models/state.model';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.css'],
    standalone: false
})
export class TableComponent<T> implements AfterContentInit, OnDestroy {
  @ViewChild(MatTable, { static: true })
  private _table: MatTable<T>;

  @ViewChild(MatMenuTrigger)
  private _trigger: MatMenuTrigger;

  @ContentChildren(MatColumnDef)
  private _columnDefs: QueryList<MatColumnDef>;

  @ContentChildren(FilterComponent, { descendants: true })
  private _filterableColumnComponents: QueryList<FilterComponent>;

  private _columnFilterChangedSubscriptions: Subscription[] = [];

  private _formGroup: FormGroup;

  get formGroup(): FormGroup {
    return this._formGroup;
  }

  private _filterFormGroup: FormGroup;

  get filterFormGroup(): FormGroup {
    return this._filterFormGroup;
  }

  private _textFilterChanged: Subject<any> = new Subject<any>();

  constructor() {
    this._state = new StateModel();

    this._formGroup = new FormGroup({
      value: new FormControl(null, Validators.maxLength(255)),
    });

    this._formGroup.controls.value.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => this._formGroup.valid)
      )
      .subscribe((newText: string) => {
        this.applyTextFilter(newText);
      });

    this._filterFormGroup = new FormGroup({
      filterField: new FormControl('', Validators.required),
      filterComparator: new FormControl('', Validators.required),
      filterValue: new FormControl('', Validators.required),
    });
  }

  private _headers: string[] = [];

  @Input()
  set headers(value: string[]) {
    this._headers = value;
  }

  get headers(): string[] {
    return this._headers;
  }

  @Input() hideActionsButtons: boolean = false;
  @Input() hideHeader: boolean = false;

  private _filterColumns: FilterColumn[] = [];
  private _hideSearch: boolean = false;

  @Input()
  set hideSearch(value: boolean) {
    this._hideSearch = value;
  }

  get hideSearch(): boolean {
    return this._hideSearch;
  }

  private _showRefresh: boolean = false;

  @Input()
  set showRefresh(value: boolean) {
    this._showRefresh = value;
  }

  get showRefresh(): boolean {
    return this._showRefresh;
  }

  private _showChipList: boolean = true;

  @Input()
  set showChipList(value: boolean) {
    this._showChipList = value;
  }

  get showChipList(): boolean {
    return this._showChipList;
  }

  private _filters: string[] = [];

  @Input()
  set filters(value: string[]) {
    this._filters = value;
  }

  get filters(): string[] {
    return this._filters;
  }

  @Input()
  set filterColumns(value: FilterColumn[]) {
    this._filterColumns = value;
  }

  get filterColumns(): FilterColumn[] {
    return this._filterColumns;
  }

  get comparators(): string[] {
    if (this.isStringFilter()) {
      return ['Contains', 'NotContains', 'Equals', 'NotEquals', 'In'];
    } else if (this.isDateTimeFilter() || this.isIntFilter() || this.isDecimalFilter()) {
      return ['Equals', 'NotEquals', 'LessThan', 'LessThanOrEquals', 'GreaterThan', 'GreaterThanOrEquals'];
    } else if (this.isStringListFilter()) {
      return ['Contains', 'NotContains'];
    }

    return [];
  }

  private _state: StateModel;

  get state(): StateModel {
    return this._state;
  }

  set state(value: StateModel) {
    this._state = value;
  }

  private _dataSource: T[] = null;

  get dataSource(): T[] {
    return this._dataSource;
  }

  set dataSource(value: T[]) {
    this._dataSource = value;
  }

  get textFilterChanged() {
    return this._textFilterChanged;
  }

  private _stateChanged: EventEmitter<StateModel> = new EventEmitter();

  @Output()
  get stateChanged() {
    return this._stateChanged;
  }

  ngAfterContentInit(): void {
    this._columnDefs.forEach(columnDef => this._table.addColumnDef(columnDef));

    setTimeout(() => {
      this.subscribeToColumnFilterChanged();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.unsubscribeToColumnFilterChanged();
  }

  onSortChanged(event: Sort) {
    this._state.sort.key = event.active;
    this._state.sort.direction = event.direction;

    this.stateChanged.emit(this.state);
  }

  onPageChanged(event: PageEvent) {
    this._state.paginator.pageSize = event.pageSize;
    this._state.paginator.pageIndex = event.pageIndex;

    this.stateChanged.emit(this.state);
  }

  onFilterChanged(event: IFilterEvent) {
    // Apply the filter only if it has value
    this._state.paginator.pageIndex = 0;

    if (event.value) {
      this._state.columnFilters.set(event.key, event);
    } else {
      // If the filter value is null, remove the property given it has been cleared
      this._state.columnFilters.delete(event.key);
    }

    this.stateChanged.emit(this.state);
  }

  onRefresh() {
    this.stateChanged.emit(this.state);
  }

  private subscribeToColumnFilterChanged() {
    this._columnFilterChangedSubscriptions = [];

    this._filterableColumnComponents.forEach(component => {
      component.value = this.state.columnFilters.get(component.key)?.value;
      this._columnFilterChangedSubscriptions.push(
        component.filterChanged.subscribe((event: IFilterEvent) => {
          this.onFilterChanged(event);
        })
      );
    });
  }

  private unsubscribeToColumnFilterChanged(): void {
    if (this._columnFilterChangedSubscriptions) {
      this._columnFilterChangedSubscriptions.forEach((item: Subscription) => {
        item.unsubscribe();
      });
    }

    this._columnFilterChangedSubscriptions = [];
  }

  applyTextFilter(textFilterValue: string) {
    this._state.paginator.pageIndex = 0;
    this._state.textFilter = textFilterValue;

    this.stateChanged.emit(this.state);
  }

  onTextFilterReset(): void {
    this._formGroup.reset();

    this._state.paginator.pageIndex = 0;
    this._state.textFilter = '';
  }

  onRemoveFilter(filter: FilterModel): void {
    const index = this.state.filters.findIndex(
      f => f.category === filter.category && f.operator === filter.operator && f.value === filter.value
    );

    if (index > -1) {
      this.state.filters.splice(index, 1);
      this.stateChanged.emit(this.state);
    }
  }

  onAddFilter(): void {
    if (this.filterFormGroup.valid) {
      let filterValue;
      if (this.isIntFilter()) {
        filterValue = Number.parseInt(this.filterFormGroup.controls.filterValue.value);
      } else if (this.isDateTimeFilter()) {
        filterValue = new Date(this.filterFormGroup.controls.filterValue.value).toISOString();
      } else {
        filterValue = this.filterFormGroup.controls.filterValue.value;
      }

      this.state.filters.push({
        title: this.filterFormGroup.controls.filterField.value.title,
        category: this.filterFormGroup.controls.filterField.value.name,
        operator: this.filterFormGroup.controls.filterComparator.value,
        value: filterValue,
      });
      this.stateChanged.emit(this.state);
      this.filterFormGroup.reset();
      this._filterType = FilterColumnType.string;
      this._filterFormGroup.controls['filterValue'].setValidators([Validators.required, Validators.maxLength(50)]);
      this._trigger.closeMenu();
    }
  }

  _filterType: FilterColumnType;

  onFilterFieldChange(event: any): void {
    this._filterType = event.value.type;
    switch (this._filterType) {
      case FilterColumnType.int:
        this._filterFormGroup.controls['filterValue'].setValidators([
          Validators.required,
          Validators.min(-999999999),
          Validators.max(999999999),
        ]);
        break;
      case FilterColumnType.decimal:
        this._filterFormGroup.controls['filterValue'].setValidators([
          Validators.required,
          Validators.min(-999999999),
          Validators.max(999999999),
        ]);
        break;
      case FilterColumnType.dateTime:
        this._filterFormGroup.controls['filterValue'].setValidators([Validators.required]);
        break;
      default:
        this._filterFormGroup.controls['filterValue'].setValidators([Validators.required, Validators.maxLength(50)]);
        break;
    }
  }

  isStringFilter(): boolean {
    return this._filterType === FilterColumnType.string || !this._filterType;
  }

  isDateTimeFilter(): boolean {
    return this._filterType === FilterColumnType.dateTime;
  }

  isIntFilter(): boolean {
    return this._filterType === FilterColumnType.int;
  }

  isDecimalFilter(): boolean {
    return this._filterType === FilterColumnType.decimal;
  }

  isStringListFilter(): boolean {
    return this._filterType === FilterColumnType.stringList;
  }
}
